import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Modal from '../ui/Modal';
import { Settings, Save, Database, Mail, Shield, Bell, CreditCard, Globe, Users, RefreshCw } from 'lucide-react';
import './AdminSettings.css';
import API_BASE_URL from '../../config/api';

import { devLog } from '../../utils/devLog';
/**
 * Admin Settings - Comprehensive platform configuration
 */
const AdminSettings = () => {
  const [settings, setSettings] = useState({
    platform: {
      siteName: 'FitCraft Designer Site',
      siteDescription: 'Fashion Designer Marketplace',
      maintenanceMode: false,
      registrationEnabled: true,
      defaultCommission: 3.0,
      minimumPayout: 500,
      currency: 'INR'
    },
    razorpay: {
      keyId: '',
      secret: '',
      testMode: true,
      mockMode: true,
      enableRoute: false,
      webhookSecret: ''
    },
    email: {
      host: 'smtp.gmail.com',
      port: 587,
      username: '',
      password: '',
      useTLS: true,
      fromName: 'FitCraft Designer Site'
    },
    security: {
      accessTokenLifetime: 120, // minutes
      refreshTokenLifetime: 7, // days
      maxLoginAttempts: 5,
      passwordResetExpiry: 24, // hours
      requireEmailVerification: true
    },
    notifications: {
      orderNotifications: true,
      payoutNotifications: true,
      systemAlerts: true,
      emailNotifications: true,
      smsNotifications: false
    },
    uploads: {
      maxFileSize: 10, // MB
      allowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
      maxImagesPerProduct: 6,
      enableImageCompression: true
    },
    analytics: {
      googleAnalyticsId: '',
      facebookPixelId: '',
      enableTracking: true,
      trackPurchases: true
    }
  });

  const [activeTab, setActiveTab] = useState('platform');
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [showModal, setShowModal] = useState({ open: false, type: '', message: '' });

  const settingsTabs = [
    { key: 'platform', label: 'Platform', icon: <Settings className="w-4 h-4" /> },
    { key: 'razorpay', label: 'Payments', icon: <CreditCard className="w-4 h-4" /> },
    { key: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
    { key: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { key: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { key: 'uploads', label: 'File Uploads', icon: <Database className="w-4 h-4" /> },
    { key: 'analytics', label: 'Analytics', icon: <Globe className="w-4 h-4" /> }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/settings/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      devLog.error('Error fetching settings:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE_URL}/api/admin/settings/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setShowModal({
          open: true,
          type: 'success',
          message: 'Settings saved successfully!'
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      setShowModal({
        open: true,
        type: 'error',
        message: 'Error saving settings: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const testEmailConfiguration = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/test-email/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings.email),
      });

      const result = await response.json();
      setTestResults(prev => ({
        ...prev,
        email: { success: response.ok, message: result.message }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        email: { success: false, message: error.message }
      }));
    }
  };

  const testRazorpayConfiguration = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/test-razorpay/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings.razorpay),
      });

      const result = await response.json();
      setTestResults(prev => ({
        ...prev,
        razorpay: { success: response.ok, message: result.message }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        razorpay: { success: false, message: error.message }
      }));
    }
  };

  const renderPlatformSettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Platform Configuration</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>Site Name</label>
          <Input
            value={settings.platform.siteName}
            onChange={(e) => handleInputChange('platform', 'siteName', e.target.value)}
            placeholder="FitCraft Designer Site"
          />
        </div>

        <div className="settings-field">
          <label>Site Description</label>
          <Input
            value={settings.platform.siteDescription}
            onChange={(e) => handleInputChange('platform', 'siteDescription', e.target.value)}
            placeholder="Fashion Designer Marketplace"
          />
        </div>

        <div className="settings-field">
          <label>Platform Commission (%)</label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="50"
            value={settings.platform.defaultCommission}
            onChange={(e) => handleInputChange('platform', 'defaultCommission', parseFloat(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Minimum Payout (₹)</label>
          <Input
            type="number"
            min="100"
            value={settings.platform.minimumPayout}
            onChange={(e) => handleInputChange('platform', 'minimumPayout', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Currency</label>
          <Select
            value={settings.platform.currency}
            onChange={(e) => handleInputChange('platform', 'currency', e.target.value)}
            options={[
              { value: 'INR', label: 'Indian Rupee (₹)' },
              { value: 'USD', label: 'US Dollar ($)' },
              { value: 'EUR', label: 'Euro (€)' }
            ]}
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.platform.maintenanceMode}
              onChange={(e) => handleInputChange('platform', 'maintenanceMode', e.target.checked)}
            />
            Maintenance Mode
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.platform.registrationEnabled}
              onChange={(e) => handleInputChange('platform', 'registrationEnabled', e.target.checked)}
            />
            Registration Enabled
          </label>
        </div>
      </div>
    </div>
  );

  const renderRazorpaySettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Razorpay Configuration</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>Key ID</label>
          <Input
            value={settings.razorpay.keyId}
            onChange={(e) => handleInputChange('razorpay', 'keyId', e.target.value)}
            placeholder="rzp_test_..."
            type="password"
          />
        </div>

        <div className="settings-field">
          <label>Secret Key</label>
          <Input
            value={settings.razorpay.secret}
            onChange={(e) => handleInputChange('razorpay', 'secret', e.target.value)}
            placeholder="Secret Key"
            type="password"
          />
        </div>

        <div className="settings-field">
          <label>Webhook Secret</label>
          <Input
            value={settings.razorpay.webhookSecret}
            onChange={(e) => handleInputChange('razorpay', 'webhookSecret', e.target.value)}
            placeholder="Webhook Secret"
            type="password"
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.razorpay.testMode}
              onChange={(e) => handleInputChange('razorpay', 'testMode', e.target.checked)}
            />
            Test Mode
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.razorpay.mockMode}
              onChange={(e) => handleInputChange('razorpay', 'mockMode', e.target.checked)}
            />
            Mock Mode (Development)
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.razorpay.enableRoute}
              onChange={(e) => handleInputChange('razorpay', 'enableRoute', e.target.checked)}
            />
            Enable Route API
          </label>
        </div>

        <div className="settings-actions">
          <Button
            onClick={testRazorpayConfiguration}
            variant="secondary"
            className="test-button"
          >
            <RefreshCw className="w-4 h-4" />
            Test Configuration
          </Button>

          {testResults.razorpay && (
            <div className={`test-result ${testResults.razorpay.success ? 'success' : 'error'}`}>
              {testResults.razorpay.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Email Configuration</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>SMTP Host</label>
          <Input
            value={settings.email.host}
            onChange={(e) => handleInputChange('email', 'host', e.target.value)}
            placeholder="smtp.gmail.com"
          />
        </div>

        <div className="settings-field">
          <label>Port</label>
          <Input
            type="number"
            value={settings.email.port}
            onChange={(e) => handleInputChange('email', 'port', parseInt(e.target.value))}
            placeholder="587"
          />
        </div>

        <div className="settings-field">
          <label>Username/Email</label>
          <Input
            value={settings.email.username}
            onChange={(e) => handleInputChange('email', 'username', e.target.value)}
            placeholder="your-email@gmail.com"
          />
        </div>

        <div className="settings-field">
          <label>App Password</label>
          <Input
            type="password"
            value={settings.email.password}
            onChange={(e) => handleInputChange('email', 'password', e.target.value)}
            placeholder="16-character app password"
          />
        </div>

        <div className="settings-field">
          <label>From Name</label>
          <Input
            value={settings.email.fromName}
            onChange={(e) => handleInputChange('email', 'fromName', e.target.value)}
            placeholder="FitCraft Designer Site"
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.email.useTLS}
              onChange={(e) => handleInputChange('email', 'useTLS', e.target.checked)}
            />
            Use TLS/SSL
          </label>
        </div>

        <div className="settings-actions">
          <Button
            onClick={testEmailConfiguration}
            variant="secondary"
            className="test-button"
          >
            <RefreshCw className="w-4 h-4" />
            Test Email
          </Button>

          {testResults.email && (
            <div className={`test-result ${testResults.email.success ? 'success' : 'error'}`}>
              {testResults.email.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Security Configuration</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>Access Token Lifetime (minutes)</label>
          <Input
            type="number"
            min="15"
            max="1440"
            value={settings.security.accessTokenLifetime}
            onChange={(e) => handleInputChange('security', 'accessTokenLifetime', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Refresh Token Lifetime (days)</label>
          <Input
            type="number"
            min="1"
            max="30"
            value={settings.security.refreshTokenLifetime}
            onChange={(e) => handleInputChange('security', 'refreshTokenLifetime', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Max Login Attempts</label>
          <Input
            type="number"
            min="3"
            max="20"
            value={settings.security.maxLoginAttempts}
            onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Password Reset Expiry (hours)</label>
          <Input
            type="number"
            min="1"
            max="72"
            value={settings.security.passwordResetExpiry}
            onChange={(e) => handleInputChange('security', 'passwordResetExpiry', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.security.requireEmailVerification}
              onChange={(e) => handleInputChange('security', 'requireEmailVerification', e.target.checked)}
            />
            Require Email Verification
          </label>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Notification Settings</h3>

      <div className="settings-grid">
        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.notifications.orderNotifications}
              onChange={(e) => handleInputChange('notifications', 'orderNotifications', e.target.checked)}
            />
            Order Notifications
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.notifications.payoutNotifications}
              onChange={(e) => handleInputChange('notifications', 'payoutNotifications', e.target.checked)}
            />
            Payout Notifications
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.notifications.systemAlerts}
              onChange={(e) => handleInputChange('notifications', 'systemAlerts', e.target.checked)}
            />
            System Alerts
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.notifications.emailNotifications}
              onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
            />
            Email Notifications
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.notifications.smsNotifications}
              onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
            />
            SMS Notifications
          </label>
        </div>
      </div>
    </div>
  );

  const renderUploadSettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">File Upload Settings</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>Max File Size (MB)</label>
          <Input
            type="number"
            min="1"
            max="100"
            value={settings.uploads.maxFileSize}
            onChange={(e) => handleInputChange('uploads', 'maxFileSize', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Max Images Per Product</label>
          <Input
            type="number"
            min="1"
            max="20"
            value={settings.uploads.maxImagesPerProduct}
            onChange={(e) => handleInputChange('uploads', 'maxImagesPerProduct', parseInt(e.target.value))}
          />
        </div>

        <div className="settings-field">
          <label>Allowed Formats</label>
          <Input
            value={settings.uploads.allowedFormats.join(', ')}
            onChange={(e) => handleInputChange('uploads', 'allowedFormats', e.target.value.split(',').map(f => f.trim()))}
            placeholder="jpg, jpeg, png, webp"
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.uploads.enableImageCompression}
              onChange={(e) => handleInputChange('uploads', 'enableImageCompression', e.target.checked)}
            />
            Enable Image Compression
          </label>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsSettings = () => (
    <div className="settings-section">
      <h3 className="settings-section__title">Analytics Configuration</h3>

      <div className="settings-grid">
        <div className="settings-field">
          <label>Google Analytics ID</label>
          <Input
            value={settings.analytics.googleAnalyticsId}
            onChange={(e) => handleInputChange('analytics', 'googleAnalyticsId', e.target.value)}
            placeholder="G-XXXXXXXXXX"
          />
        </div>

        <div className="settings-field">
          <label>Facebook Pixel ID</label>
          <Input
            value={settings.analytics.facebookPixelId}
            onChange={(e) => handleInputChange('analytics', 'facebookPixelId', e.target.value)}
            placeholder="123456789012345"
          />
        </div>

        <div className="settings-field-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.analytics.enableTracking}
              onChange={(e) => handleInputChange('analytics', 'enableTracking', e.target.checked)}
            />
            Enable Tracking
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.analytics.trackPurchases}
              onChange={(e) => handleInputChange('analytics', 'trackPurchases', e.target.checked)}
            />
            Track Purchases
          </label>
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => {
    switch (activeTab) {
      case 'platform': return renderPlatformSettings();
      case 'razorpay': return renderRazorpaySettings();
      case 'email': return renderEmailSettings();
      case 'security': return renderSecuritySettings();
      case 'notifications': return renderNotificationSettings();
      case 'uploads': return renderUploadSettings();
      case 'analytics': return renderAnalyticsSettings();
      default: return renderPlatformSettings();
    }
  };

  return (
    <div className="admin-settings">
      <div className="admin-settings__header">
        <div className="admin-settings__title">
          <Settings className="w-6 h-6 text-[#ca7751]" />
          <h1>Platform Settings</h1>
        </div>
        <Button onClick={handleSave} loading={saving} className="save-button">
          <Save className="w-4 h-4" />
          Save Changes
        </Button>
      </div>

      <div className="admin-settings__container">
        <div className="admin-settings__sidebar">
          {settingsTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`settings-tab ${activeTab === tab.key ? 'active' : ''}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="admin-settings__content">
          <Card className="settings-card">
            {renderSettingsContent()}
          </Card>
        </div>
      </div>

      {/* Success/Error Modal */}
      <Modal
        isOpen={showModal.open}
        onClose={() => setShowModal({ open: false, type: '', message: '' })}
        title={showModal.type === 'success' ? 'Success' : 'Error'}
      >
        <p>{showModal.message}</p>
      </Modal>
    </div>
  );
};

export default AdminSettings;